import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Maximize2,
  Play,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ApiError, patchDocument } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import type { Sheet } from "@/api/client";
import { isArrow, nextByArrow } from "@/flow/arrows";
import { StepHeader } from "@/flow/StepHeader";
import { elapsed } from "@/flow/progress";
import {
  ROLE_LABEL,
  ROLE_ONE,
  ROLE_ORDER,
  estimateCost,
  filterSheets,
  groupSheets,
  sheetName,
} from "@/flow/sheets";
import type { SheetFilter } from "@/flow/sheets";
import { useDocument } from "@/hooks/useDocument";
import { useRuns } from "@/hooks/useRuns";
import { useSheets } from "@/hooks/useSheets";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Dialog } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import { Card, Spinner } from "@/ui/primitives";
import { SplitView } from "@/viewer/SplitView";

const NO_INSTANCES = [] as const;
const NO_PAINT = new Map<string, never>();

interface CardProps {
  sheet: Sheet;
  selected: boolean;
  onToggle: (pageIndex: number) => void;
  onPreview: (pageIndex: number) => void;
  /** A role that may not choose sheets sees the choice but cannot change it. */
  disabled: boolean;
}

function PickCard({ sheet, selected, onToggle, onPreview, disabled }: CardProps) {
  const name = sheetName(sheet);
  return (
    <li
      data-sheet={sheet.page_index}
      className={cn(
        "group relative overflow-hidden rounded-card border bg-surface shadow-card transition-all",
        selected
          ? "border-accent ring-2 ring-accent/25"
          : "border-line hover:border-line-strong hover:shadow-pop",
      )}
    >
      <button
        type="button"
        className="relative block aspect-[4/3] w-full overflow-hidden border-b border-line bg-white"
        onClick={() => onPreview(sheet.page_index)}
        aria-label={`Preview ${name}`}
        tabIndex={-1}
      >
        <img
          src={sheet.thumb_url}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-contain p-1.5 transition-transform group-hover:scale-[1.02]"
        />
        <span className="absolute right-2 bottom-2 grid size-7 place-items-center rounded-md bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100">
          <Maximize2 aria-hidden="true" className="size-3.5" />
        </span>
      </button>
      <label className="flex cursor-pointer items-start gap-2.5 px-3 pt-2.5">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 cursor-pointer accent-[var(--color-accent)]"
          checked={selected}
          disabled={disabled}
          onChange={() => onToggle(sheet.page_index)}
          aria-label={`Use ${name}${sheet.sheet_title ? `, ${sheet.sheet_title}` : ""}`}
        />
        <span className="min-w-0">
          <span className="block font-mono text-[13px] font-medium text-ink">{name}</span>
          {sheet.sheet_title ? (
            <span className="block truncate text-[13px] text-ink-2" title={sheet.sheet_title}>
              {sheet.sheet_title}
            </span>
          ) : null}
        </span>
      </label>
      <p className="line-clamp-2 px-3 pt-1 pb-3 pl-9 text-xs text-muted">
        {sheet.suggested && sheet.reason ? sheet.reason : ROLE_ONE[sheet.role]}
      </p>
    </li>
  );
}

const usd = (value: number) => `$${value.toFixed(2)}`;

/**
 * Step two: the system proposes which sheets a take-off works from, the estimator adds
 * or removes, then confirms. Confirming is what starts the run.
 */
export function SelectSheetsScreen() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { doc, error: docError } = useDocument(id);
  const state = useSheets(id, doc?.status);
  const runs = useRuns(id);
  const canChoose = useCan("documents:upload");
  const canAllow = useCan("llm_policy:change");
  const [filter, setFilter] = useState<SheetFilter>("all");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const [policy, setPolicy] = useState<string | null>(null);
  const [who, setWho] = useState("");
  const [recording, setRecording] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const grid = useRef<HTMLDivElement>(null);

  const reading = !state.ready;
  useEffect(() => {
    if (!reading) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [reading]);

  const visible = useMemo(
    () => filterSheets(state.sheets, filter, search, state.selected),
    [state.sheets, filter, search, state.selected],
  );
  const groups = useMemo(() => groupSheets(visible), [visible]);
  const cost = useMemo(
    () => estimateCost(state.selected, state.classified),
    [state.selected, state.classified],
  );
  const counts = useMemo(() => {
    const by: Partial<Record<SheetFilter, number>> = {
      all: state.sheets.length,
      selected: state.selected.size,
      suggested: state.sheets.filter((s) => s.suggested).length,
    };
    for (const role of ROLE_ORDER) {
      by[role] = state.sheets.filter((s) => s.role === role).length;
    }
    return by;
  }, [state.sheets, state.selected]);

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!isArrow(event.key) || target.tagName !== "INPUT" || !grid.current) {
      return;
    }
    const boxes = [...grid.current.querySelectorAll<HTMLInputElement>("li[data-sheet] input")];
    const current = boxes.indexOf(target as HTMLInputElement);
    if (current < 0) {
      return;
    }
    event.preventDefault();
    const spots = boxes.map((box) => {
      const rect = box.closest("li")!.getBoundingClientRect();
      return { left: rect.left, top: rect.top };
    });
    boxes[nextByArrow(spots, current, event.key)]?.focus();
  }, []);

  const confirm = useCallback(async () => {
    const run = await state.confirm();
    if (run) {
      await navigate(`/documents/${id}/runs/${run.id}/progress`);
    }
  }, [state, navigate, id]);

  const record = useCallback(async () => {
    const note = who.trim();
    if (!note) {
      return;
    }
    setRecording(true);
    setPolicyError(null);
    try {
      const next = await patchDocument(id, { llm_policy: "allowed", note });
      setPolicy(next.llm_policy);
    } catch (err) {
      setPolicyError(err instanceof ApiError ? err.detail : "could not record the permission");
    } finally {
      setRecording(false);
    }
  }, [id, who]);

  if (docError && !doc) {
    return (
      <main className="min-h-full bg-bg">
        <StepHeader step="sheets" />
        <div className="mx-auto max-w-3xl px-4 py-10">
          <Notice tone="danger">{docError}</Notice>
          <Link className="mt-4 inline-block text-accent" to="/sets">
            Back to your sets
          </Link>
        </div>
      </main>
    );
  }

  const llmPolicy = policy ?? doc?.llm_policy ?? "restricted";
  const restricted = llmPolicy !== "allowed";
  // Sheets already classified need no model, so a restricted set can still run on them;
  // so can one whose model answers are recorded, which replays them (ADR-0008).
  const replay = doc?.llm_replay_available ?? false;
  const needsPermission =
    restricted && !replay && (cost.sheets > 0 || state.confirmError?.status === 403);
  const nothing = state.selected.size === 0;
  const latest = runs.run;
  const previewSheet =
    preview === null ? null : (state.sheets.find((s) => s.page_index === preview) ?? null);
  const order = visible.map((s) => s.page_index);
  const step = (by: 1 | -1) => {
    if (preview === null) {
      return;
    }
    const next = order[order.indexOf(preview) + by];
    if (next !== undefined) {
      setPreview(next);
    }
  };

  const chips: { key: SheetFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "selected", label: "Selected" },
    { key: "suggested", label: "Suggested" },
    ...ROLE_ORDER.filter((role) => (counts[role] ?? 0) > 0).map((role) => ({
      key: role,
      label: ROLE_LABEL[role],
    })),
  ];

  return (
    <main className="flex min-h-full flex-col bg-bg">
      <StepHeader
        step="sheets"
        documentId={id}
        filename={doc?.filename}
        runId={latest?.id}
        runFinished={latest?.status === "done"}
      />

      {reading ? (
        <div className="mx-auto w-full max-w-xl px-4 py-16">
          <Card className="p-6" role="status" aria-live="polite">
            <div className="flex items-center gap-3">
              {doc?.status === "failed" ? (
                <CircleAlert aria-hidden="true" className="size-5 text-danger" />
              ) : (
                <Spinner className="text-accent" />
              )}
              <h1 className="text-lg font-semibold text-ink">
                {doc?.status === "failed" ? "The set could not be read" : "Reading the set"}
              </h1>
            </div>
            <p className="mt-2 text-sm text-muted">
              Each sheet is rendered and its title block read. Sheets appear here as soon as the set
              is ready.
            </p>
            <dl className="mt-5 grid grid-cols-3 gap-3 text-sm">
              <Fact label="Sheets" value={String(doc?.page_count ?? "…")} />
              <Fact label="Elapsed" value={elapsed(doc?.created_at ?? null, now) || "…"} />
              <Fact
                label="State"
                value={
                  doc?.status === "failed"
                    ? "Failed"
                    : doc?.status === "uploaded"
                      ? "Waiting to start"
                      : "Reading"
                }
              />
            </dl>
            {state.error ? <p className="mt-3 text-sm text-danger">{state.error}</p> : null}
          </Card>
        </div>
      ) : (
        <>
          <div className="mx-auto w-full max-w-[1600px] flex-1 px-4 pt-6 pb-32 lg:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-xl font-semibold tracking-tight text-ink">
                  Choose the sheets for this take-off
                </h1>
                <p className="mt-1 text-sm text-muted">
                  {counts.suggested
                    ? `${counts.suggested} of ${counts.all} sheets are suggested from their titles. Add or remove any before you confirm.`
                    : "No sheet could be suggested from its title. Pick the elevations and plans the take-off should use."}
                </p>
              </div>
              <label className="relative w-full sm:w-72">
                <span className="sr-only">Find a sheet by number or title</span>
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted"
                />
                <input
                  type="search"
                  value={search}
                  placeholder="Find a sheet, e.g. A5.02 or elevation"
                  onChange={(event) => setSearch(event.target.value)}
                  className="h-9 w-full rounded-lg border border-line-strong bg-surface pr-3 pl-8 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none"
                />
              </label>
            </div>

            {latest ? (
              <Notice
                className="mt-4"
                tone={latest.status === "done" ? "ok" : runs.active ? "accent" : "warn"}
              >
                {latest.status === "done" ? (
                  <>
                    This set already has a take-off.{" "}
                    <Link
                      className="font-medium underline"
                      to={`/documents/${id}/runs/${latest.id}/takeoff`}
                    >
                      Open the take-off
                    </Link>
                  </>
                ) : runs.active ? (
                  <>
                    A run is in progress.{" "}
                    <Link
                      className="font-medium underline"
                      to={`/documents/${id}/runs/${latest.id}/progress`}
                    >
                      See its progress
                    </Link>
                  </>
                ) : (
                  <>
                    The last run stopped early.{" "}
                    <Link
                      className="font-medium underline"
                      to={`/documents/${id}/runs/${latest.id}/progress`}
                    >
                      See what happened
                    </Link>
                  </>
                )}
              </Notice>
            ) : null}

            <div className="mt-5 flex flex-wrap gap-1.5" role="group" aria-label="Show">
              {chips.map((chip) => {
                const on = filter === chip.key;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setFilter(chip.key)}
                    className={cn(
                      "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-colors",
                      on
                        ? "border-accent bg-accent text-accent-ink"
                        : "border-line bg-surface text-ink-2 hover:border-line-strong hover:text-ink",
                    )}
                  >
                    {chip.label}
                    <span
                      className={cn(
                        "tabular rounded-full px-1.5 text-[11px]",
                        on ? "bg-white/20" : "bg-sunken text-muted",
                      )}
                    >
                      {counts[chip.key] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-8" ref={grid} onKeyDown={onKeyDown}>
              {groups.map((group) => {
                const indexes = group.sheets.map((s) => s.page_index);
                const allOn = indexes.every((i) => state.selected.has(i));
                return (
                  <section key={group.key} aria-label={group.title}>
                    <header className="mb-3 flex items-center justify-between gap-3">
                      <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                        {group.title}
                        <Badge>{group.sheets.length}</Badge>
                      </h2>
                      {canChoose ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => state.setMany(indexes, !allOn)}
                        >
                          {allOn ? "Clear this group" : "Select all in group"}
                        </Button>
                      ) : null}
                    </header>
                    <ul className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
                      {group.sheets.map((sheet) => (
                        <PickCard
                          key={sheet.page_index}
                          sheet={sheet}
                          selected={state.selected.has(sheet.page_index)}
                          onToggle={state.toggle}
                          onPreview={setPreview}
                          disabled={!canChoose}
                        />
                      ))}
                    </ul>
                  </section>
                );
              })}
              {groups.length === 0 ? (
                <Card className="px-6 py-12 text-center text-sm text-muted">
                  No sheet matches. Clear the search or choose another filter.
                </Card>
              ) : null}
            </div>
          </div>

          <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 backdrop-blur">
            <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 lg:px-6">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink" aria-live="polite">
                  <strong className="tabular">{state.selected.size}</strong> of{" "}
                  <span className="tabular">{state.sheets.length}</span> sheets selected
                </p>
                <p className="text-[13px] text-muted">
                  {cost.sheets === 0
                    ? nothing
                      ? "Select at least one sheet"
                      : "These sheets are already classified, so this run calls no model"
                    : restricted && replay
                      ? "Recorded model answers are replayed, so this run sends nothing anywhere"
                      : `About ${usd(cost.usd)} in model calls (estimate: ${cost.sheets} sheets at about $0.05 each)`}
                </p>
                {state.saveError ? (
                  <p className="text-[13px] text-danger" role="alert">
                    {state.saveError}
                  </p>
                ) : null}
              </div>

              {!canChoose ? (
                <p className="text-[13px] text-muted">
                  Your role can look at the sheets but not choose them or start a run.
                </p>
              ) : needsPermission && !canAllow ? (
                <p className="max-w-md text-[13px] text-ink-2">
                  These drawings have not been sent anywhere. An owner or admin records permission
                  to send this set to the model before it can run.
                </p>
              ) : needsPermission ? (
                <form
                  className="flex w-full flex-col gap-2 rounded-lg border border-warn/40 bg-warn-soft p-3 lg:w-auto lg:max-w-3xl"
                  aria-label="Permission to send drawings to the model"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void record();
                  }}
                >
                  <p className="flex items-start gap-2 text-[13px] text-ink-2">
                    <ShieldAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warn" />
                    <span>
                      <strong className="text-ink">
                        These drawings have not been sent anywhere.
                      </strong>{" "}
                      Reading sheets uses a hosted model, and nothing leaves this computer until
                      permission is recorded for this set.
                    </span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex min-w-0 flex-1 items-center gap-2 text-[13px] text-ink-2">
                      <span className="shrink-0">Who gave permission</span>
                      <input
                        type="text"
                        value={who}
                        required
                        placeholder="Name, company and date"
                        onChange={(event) => setWho(event.target.value)}
                        className="h-8 min-w-48 flex-1 rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none"
                      />
                    </label>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={!who.trim() || recording}
                    >
                      {recording ? "Recording…" : "Record permission and continue"}
                    </Button>
                  </div>
                  {policyError ? (
                    <p className="text-[13px] text-danger" role="alert">
                      {policyError}
                    </p>
                  ) : null}
                </form>
              ) : (
                <div className="flex items-center gap-3">
                  {state.confirmError ? (
                    <p className="max-w-md text-[13px] text-danger" role="alert">
                      {state.confirmError.detail}
                    </p>
                  ) : null}
                  <Button
                    variant="primary"
                    size="lg"
                    disabled={nothing || state.confirming || runs.active}
                    onClick={() => void confirm()}
                  >
                    {state.confirming ? <Spinner /> : <Play aria-hidden="true" />}
                    {state.confirming
                      ? "Starting…"
                      : runs.active
                        ? "Run in progress"
                        : "Confirm and run"}
                  </Button>
                </div>
              )}
            </div>
          </footer>
        </>
      )}

      {previewSheet ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setPreview(null);
            }
          }}
          size="sheet"
          label={`Preview of ${sheetName(previewSheet)}`}
          title={
            <span className="flex items-center gap-2">
              <span className="font-mono text-[13px] text-accent">{sheetName(previewSheet)}</span>
              <span className="truncate">{previewSheet.sheet_title}</span>
            </span>
          }
          actions={
            <div className="flex items-center gap-1">
              <label className="mr-2 flex items-center gap-2 rounded-md border border-line px-2.5 py-1 text-[13px] text-ink-2">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-accent)]"
                  checked={state.selected.has(previewSheet.page_index)}
                  disabled={!canChoose}
                  onChange={() => state.toggle(previewSheet.page_index)}
                />
                Use in the take-off
              </label>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Previous sheet"
                onClick={() => step(-1)}
                disabled={order[0] === preview}
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Next sheet"
                onClick={() => step(1)}
                disabled={order[order.length - 1] === preview}
              >
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          }
        >
          <SplitView
            src={previewSheet.thumb_url}
            srcLarge={previewSheet.thumb_url.replace(/l0\.png$/, "l1.png")}
            name={sheetName(previewSheet)}
            instances={NO_INSTANCES}
            paint={NO_PAINT}
            layout="original"
          />
        </Dialog>
      ) : null}
    </main>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-sunken px-3 py-2">
      <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">{label}</dt>
      <dd className="tabular mt-0.5 font-medium text-ink">{value}</dd>
    </div>
  );
}
